import { html } from '@beunity/lit-components';
import BaseMenu from '@beunity/lit-components/components/base_menu/element.js';
import translations from './translations.js';
import '@beunity/lit-components/components/dot_menu/element.js';

/**
 * The dot-dot-dot menu for communities offerings
 *
 * @prop resourceName - The name of the resource (offering)
 * @prop offeringId - The id of a offering
 * @prop editOn - Whether the edit option is enabled
 * @prop editUrl - The URL to edit the offering
 * @prop withdrawSupportOn - Whether the withdraw support option is enabled
 * @prop withdrawSupportUrl - The URL to withdraw support for the offering
 * @prop deleteOn - Whether the delete option is enabled
 * @prop deleteUrl - The URL to delete the offering
 */

export default class GroupOfferingMenu extends BaseMenu {
  /** @internal */
  static translations = translations;

  static properties = {
    resourceName: { type: String },
    offeringId: { type: Number },
    editOn: { type: Boolean },
    editUrl: { type: String },
    withdrawSupportOn: { type: Boolean },
    withdrawSupportUrl: { type: String },
    deleteOn: { type: Boolean },
    deleteUrl: { type: String },
  };

  /** @internal */
  get instanceId() {
    return this.offeringId;
  }

  /** @internal */
  get editItem() {
    return {
      title: this.t('actions.edit'),
      action: this.actionFor('offeringExecJSFromResponse', [this.editUrl]),
      icon: 'pencil',
      partName: 'edit',
    };
  }

  /** @internal */
  get deleteItem() {
    return {
      title: this.t('actions.delete'),
      action: this.actionFor('offerDelete'),
      role: 'destructive',
      icon: 'trash-can',
      partName: 'delete destructive',
    };
  }

  /** @internal */
  get withdrawSupportItem() {
    return {
      title: this.t('actions.withdraw_support'),
      action: this.actionFor('withdrawSupport'),
      role: 'destructive',
      icon: 'arrow-left-from-bracket',
      partName: 'withdraw destructive',
    };
  }

  /** @internal */
  get divider() {
    return {
      isDivider: true,
    };
  }

  /** @internal */
  get dividerOn() {
    return this.editOn && (this.withdrawSupportOn || this.deleteOn);
  }

  /** @internal */
  get items() {
    return [
      this.editOn && this.editItem,
      this.dividerOn && this.divider,
      this.withdrawSupportOn && this.withdrawSupportItem,
      this.deleteOn && this.deleteItem,
    ].filter(Boolean);
  }

  /** @internal */
  get dotMenuConfig() {
    return {
      buttons: this.items,
      header: this.resourceName,
      showCancel: true,
    };
  }

  actionFor(method, args) {
    return {
      menu: 'bu-group-offering-menu',
      id: this.instanceId,
      method: method,
      ...(args && { args: args }),
    };
  }

  offeringExecJSFromResponse(url) {
    this.commonActions.execJSFromResponse(url);
  }

  async withdrawSupport() {
    if (!this.commonActions.confirmDialog(this.t('are_you_sure'))) {
      return false;
    }

    try {
      await this.commonActions.delete(this.withdrawSupportUrl);
    } catch {
      this.commonActions.displaySnackBarError(
        this.t('snack.generic_error_msg')
      );
    }
  }

  async offerDelete() {
    if (!this.commonActions.confirmDialog(this.t('are_you_sure'))) {
      return false;
    }

    try {
      await this.commonActions.delete(this.deleteUrl);
    } catch {
      this.commonActions.displaySnackBarError(
        this.t('snack.generic_error_msg')
      );
    }
  }

  render() {
    return html` <ef-dot-menu .config=${this.dotMenuConfig}> </ef-dot-menu> `;
  }
}

GroupOfferingMenu.registerTagName('bu-group-offering-menu');
GroupOfferingMenu.registerCommonActions('bu-group-offering-menu');

customElements.get('bu-group-offering-menu') ||
  customElements.define('bu-group-offering-menu', GroupOfferingMenu);
